import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ActionItem {
  id: string;
  milestoneIndex: number;
  actionIndex: number;
  actionText: string;
  completed: boolean;
  isLinkedToHabit: boolean;
}

export interface MilestoneWithActions {
  index: number;
  title: string;
  actions: ActionItem[];
}

interface Milestone {
  title: string;
  actions: string[];
}

export const useDevPlanActions = () => {
  const { user } = useAuth();
  const [milestones, setMilestones] = useState<MilestoneWithActions[]>([]);
  const [planId, setPlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasPlan, setHasPlan] = useState(false);

  const fetchDevPlanActions = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      // Get the active development plan
      const { data: planData, error: planError } = await supabase
        .from('development_plans')
        .select('id, milestones')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (planError) throw planError;

      if (!planData) {
        setHasPlan(false);
        setMilestones([]);
        setLoading(false);
        return;
      }

      setHasPlan(true);
      setPlanId(planData.id);

      const planMilestones = (planData.milestones as unknown as Milestone[]) || [];

      // Get all milestone actions for this plan
      const { data: actionsData, error: actionsError } = await supabase
        .from('milestone_actions')
        .select('id, milestone_index, action_index, completed')
        .eq('plan_id', planData.id);

      if (actionsError) throw actionsError;

      // Get habits that are linked to milestone actions
      const { data: linkedHabits, error: habitsError } = await supabase
        .from('habits')
        .select('linked_action_id')
        .not('linked_action_id', 'is', null);

      if (habitsError) throw habitsError;

      const linkedActionIds = new Set(linkedHabits?.map(h => h.linked_action_id) || []);

      // Build the milestones with actions structure
      const milestonesWithActions: MilestoneWithActions[] = planMilestones.map((m, mIndex) => {
        const actions: ActionItem[] = m.actions.map((actionText, aIndex) => {
          // Find the milestone_action record if it exists
          const actionRecord = actionsData?.find(
            a => a.milestone_index === mIndex && a.action_index === aIndex
          );

          return {
            id: actionRecord?.id || `${planData.id}-${mIndex}-${aIndex}`,
            milestoneIndex: mIndex,
            actionIndex: aIndex,
            actionText,
            completed: actionRecord?.completed || false,
            isLinkedToHabit: actionRecord ? linkedActionIds.has(actionRecord.id) : false,
          };
        });

        return {
          index: mIndex,
          title: m.title,
          actions,
        };
      });

      setMilestones(milestonesWithActions);
    } catch (error) {
      console.error('Error fetching dev plan actions:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDevPlanActions();
  }, [fetchDevPlanActions]);

  // Create or get a milestone_action record for linking
  const ensureActionRecord = async (milestoneIndex: number, actionIndex: number): Promise<string | null> => {
    if (!user || !planId) return null;

    // Check if the action record already exists
    const { data: existing, error: existingError } = await supabase
      .from('milestone_actions')
      .select('id')
      .eq('plan_id', planId)
      .eq('milestone_index', milestoneIndex)
      .eq('action_index', actionIndex)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking existing action:', existingError);
      return null;
    }

    if (existing) {
      return existing.id;
    }

    // Create new action record
    const { data: newAction, error: insertError } = await supabase
      .from('milestone_actions')
      .insert({
        user_id: user.id,
        plan_id: planId,
        milestone_index: milestoneIndex,
        action_index: actionIndex,
        completed: false,
      })
      .select('id')
      .single();

    if (insertError) {
      console.error('Error creating action record:', insertError);
      return null;
    }

    return newAction.id;
  };

  return {
    milestones,
    planId,
    loading,
    hasPlan,
    ensureActionRecord,
    refetch: fetchDevPlanActions,
  };
};
